/**
 * Adding a box to a view and removing one, as edits to what the view exposes.
 *
 * "Remove from diagram" is not "delete from model": it drops an `expose` and
 * leaves the element where it is (best-decision-012). And it is only possible
 * when the element has an expose of its own. One brought in by `X::**` cannot be
 * removed without rewriting that expose, which would take its siblings with it -
 * so that case is refused with the reason, rather than quietly doing either.
 *
 * Which exposes show an element comes from the server (`shown_by`), so the
 * coverage rule has one home, in services/views.py.
 */

import type { ViewDetail, ViewExpose } from '../../schema'

/** An expose as it is written back: the reference as written, with its suffix. */
export function exposeText(expose: ViewExpose): string {
  const suffix = expose.is_recursive ? '::**' : expose.is_wildcard ? '::*' : ''
  return `${expose.reference}${suffix}`
}

export interface Removal {
  allowed: boolean
  /** The exposes to PUT when allowed. */
  exposes: string[]
  /** Why not, when refused - shown on the disabled control. */
  reason: string
}

/**
 * What removing one element from the diagram would write, or why it cannot.
 * @param detail the view as last read
 * @param qualifiedName the element to remove
 */
export function removal(detail: ViewDetail, qualifiedName: string): Removal {
  const state = detail.states.find((entry) => entry.qualified_name === qualifiedName)
  const by = state?.shown_by ?? []
  const own = by.filter((index) => {
    const expose = detail.exposes[index]
    return expose && expose.target === qualifiedName && !expose.is_wildcard && !expose.is_recursive
  })
  const others = by.filter((index) => !own.includes(index))

  if (others.length) {
    const names = others.map((index) => exposeText(detail.exposes[index])).join(', ')
    return {
      allowed: false,
      exposes: [],
      reason: `Shown by ${names}, which brings in more than this one element. Edit that expose instead.`,
    }
  }
  if (!own.length) {
    return { allowed: false, exposes: [], reason: 'This view does not expose it.' }
  }
  return {
    allowed: true,
    exposes: detail.exposes.filter((_, index) => !own.includes(index)).map(exposeText),
    reason: '',
  }
}

/**
 * The exposes that add one element by its qualified name, or null if the view
 * already shows it. Absolute, so it resolves from wherever the view is declared.
 */
export function addition(detail: ViewDetail, qualifiedName: string): string[] | null {
  if (detail.states.some((state) => state.qualified_name === qualifiedName)) return null
  return [...detail.exposes.map(exposeText), qualifiedName]
}

/**
 * Removing a box from a view PAIRED with one machine (decision 030).
 *
 * The view shows its machine and everything in it, so there is no expose of the
 * box's own to drop: what the diagram is, is what the machine holds. Taking a
 * state off it removes the USAGE from the machine, with its transitions; the def
 * it is a usage of stays. The machine itself, and a def written inside it in the
 * older shape, are refused with the reason. `exposes` is unchanged when allowed:
 * the edit is to the machine, not to the view.
 */
export function pairedRemoval(detail: ViewDetail, qualifiedName: string): Removal {
  const machine = detail.machine
  if (!machine) return removal(detail, qualifiedName)
  const state = detail.states.find((entry) => entry.qualified_name === qualifiedName)
  const refuse = (reason: string): Removal => ({ allowed: false, exposes: [], reason })
  if (!state) return refuse('This view does not show it.')
  if (qualifiedName === machine.qualified_name) {
    return refuse(`${machine.name} is the machine this view shows, so it cannot be taken off its own diagram.`)
  }
  if (state.is_definition) {
    return refuse(
      `${state.name} is a state def written inside ${machine.name}, not a usage of one. Delete it from the model instead.`,
    )
  }
  return { allowed: true, exposes: detail.exposes.map(exposeText), reason: '' }
}

/**
 * Where a state put on a paired diagram is declared: inside the selected box, or
 * in the view's machine when nothing is selected. Null for an unpaired view.
 */
export function usageTarget(detail: ViewDetail, selected: string | null): string | null {
  const machine = detail.machine
  if (!machine) return null
  const picked = selected ? detail.states.find((state) => state.qualified_name === selected && !state.pseudo) : undefined
  return picked ? picked.qualified_name : machine.qualified_name
}

/** The defs a paired diagram may add a usage of: every state def but its own machine. */
export function usableDefs<T extends { is_definition?: unknown; qualified_name?: unknown }>(
  detail: ViewDetail,
  rows: T[],
): T[] {
  return rows.filter((row) => row.is_definition !== false && row.qualified_name !== detail.machine?.qualified_name)
}

/**
 * The selection after one of React Flow's select changes.
 *
 * Clicking one box while another is selected sends a select for the new box
 * and a deselect for the old one, in either order. Taken as "deselect means
 * nothing is picked", the second order left the new box highlighted but not
 * picked, and every control said "Select a box first" (found on issue #92).
 */
export function pickedAfter(now: string | null, id: string, selected: boolean): string | null {
  if (selected) return id
  return now === id ? null : now
}

/**
 * Whether a key press on the canvas asks for "Remove from diagram".
 *
 * Delete only. Backspace is how a field is edited, and one press too many
 * there must not take a box off the view. A key typed into a field is the
 * field's, wherever the field sits.
 */
export function isRemoveKey(key: string, target: EventTarget | null): boolean {
  if (key !== 'Delete') return false
  const typing = (target as Element | null)?.closest?.('input, textarea, select, [contenteditable="true"]')
  return !typing
}
